import React, { useCallback, useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Formik } from 'formik';
import NumberFormat from 'react-number-format';
import {
  FiBriefcase,
  FiImage,
  FiInfo,
  FiMapPin,
  FiPhone,
  FiPercent,
} from 'react-icons/fi';

import api from '../../../../services/api';
import { useToast } from '../../../../hooks/useToast';

import {
  FieldBlock,
  FooterHint,
  Grid,
  Label,
  LogoUpload,
  MaskedInputWrapper,
  ModalSubtitle,
  Section,
  SectionHead,
  Select,
  TextInput,
} from '../modalStyles';
import { GhostButton, PrimaryButton } from '../styles';

interface ISyndicateInfo {
  id: string;
  nome_fantasia: string;
  razao_social: string;
  cnpj: string;
  email: string;
  type: string;
  phone: string;
  address: string;
  address2: string;
  city_id: number;
  state: string;
  postal_code: string;
  avatar: string;
  discount_percentage: number;
}

interface IState {
  uf: string;
  nome: string;
}

interface ICity {
  id: number;
  nome: string;
}

type IProps = {
  show: boolean;
  handleShow: (value: boolean) => void;
  syndicateInfo: ISyndicateInfo | undefined;
  handleUnions: (unions: any) => void;
  states: IState[];
};

const EditSyndicateModal: React.FC<IProps> = ({
  handleShow,
  show,
  syndicateInfo,
  handleUnions,
  states,
}) => {
  const { addToast } = useToast();

  const [cities, setCities] = useState<ICity[]>([]);
  const [featuredImgLabel, setFeaturedImgLabel] = useState('Selecionar imagem');
  const [featuredImgSrc, setFeaturedImgSrc] = useState('');

  const handleImageUpload = useCallback(event => {
    if (event.target.files[0]) {
      const alt = event.target.files[0].name;
      const src = URL.createObjectURL(event.target.files[0]);
      setFeaturedImgLabel(alt);
      setFeaturedImgSrc(src);
    }
  }, []);

  const handleLoadCities = useCallback(async (uf: string) => {
    if (!uf) {
      setCities([]);
      return;
    }
    const response = await api.get(`/ibge/cities/${uf}`);
    setCities(response.data);
  }, []);

  const handleClose = useCallback(() => {
    handleShow(false);
  }, [handleShow]);

  const handleUpdateSyndicate = useCallback(
    async ({ avatar, discount_percentage: discountPercentage, ...rest }) => {
      if (!syndicateInfo) return;
      try {
        const discount_percentage =
          (typeof discountPercentage === 'string'
            ? parseFloat(discountPercentage.replace('%', ''))
            : discountPercentage) / 100;

        const syndicateData = new FormData();

        if (avatar && typeof avatar !== 'string') {
          syndicateData.append('avatar', avatar);
        }

        syndicateData.append(
          'document',
          JSON.stringify({ ...rest, discount_percentage }),
        );

        await api.put(`/unions/${syndicateInfo.id}`, syndicateData);
        await api.get('/unions').then(response => handleUnions(response.data));
        handleClose();

        addToast({
          title: 'Sucesso',
          body: 'Sindicato atualizado com sucesso.',
        });
      } catch (error) {
        addToast({
          title: 'Erro',
          body: 'Ocorreu algum erro ao tentar atualizar o sindicato.',
        });
      }
    },
    [syndicateInfo, handleUnions, addToast, handleClose],
  );

  useEffect(() => {
    if (show && syndicateInfo) {
      handleLoadCities(syndicateInfo.state);
      if (syndicateInfo.avatar) {
        setFeaturedImgSrc(syndicateInfo.avatar);
        setFeaturedImgLabel('Imagem atual');
      } else {
        setFeaturedImgSrc('');
        setFeaturedImgLabel('Selecionar imagem');
      }
    }
  }, [show, syndicateInfo, handleLoadCities]);

  return (
    <Modal
      show={show}
      onHide={handleClose}
      aria-labelledby="contained-modal-title-vcenter"
      centered
      size="xl"
    >
      {syndicateInfo && (
        <Formik
          initialValues={{
            nome_fantasia: syndicateInfo.nome_fantasia,
            razao_social: syndicateInfo.razao_social,
            cnpj: syndicateInfo.cnpj,
            phone: syndicateInfo.phone,
            email: syndicateInfo.email,
            type: syndicateInfo.type,
            discount_percentage: syndicateInfo.discount_percentage * 100,
            address: syndicateInfo.address,
            address2: syndicateInfo.address2,
            city_id: syndicateInfo.city_id,
            state: syndicateInfo.state,
            postal_code: syndicateInfo.postal_code,
            avatar: syndicateInfo.avatar,
          }}
          enableReinitialize
          onSubmit={values => handleUpdateSyndicate(values)}
        >
          {({ values, handleSubmit, handleChange, setFieldValue }) => (
            <form onSubmit={handleSubmit}>
              <Modal.Header closeButton>
                <div>
                  <Modal.Title style={{ fontSize: 18, fontWeight: 700 }}>
                    Editar sindicato
                  </Modal.Title>
                  <ModalSubtitle>
                    Atualize os dados de identificação, contato, endereço e
                    acordo do sindicato.
                  </ModalSubtitle>
                </div>
              </Modal.Header>

              <Modal.Body style={{ background: '#fafbfc', padding: 20 }}>
                {/* 1 · Identificação */}
                <Section>
                  <SectionHead>
                    <span className="badge">
                      <FiBriefcase size={16} />
                    </span>
                    <div>
                      <h4>Identificação</h4>
                      <span className="subtitle">
                        Nome, razão social, CNPJ e categoria
                      </span>
                    </div>
                  </SectionHead>

                  <FieldBlock>
                    <Label>Nome do sindicato *</Label>
                    <TextInput
                      type="text"
                      placeholder="Ex.: Sindicato dos Trabalhadores Rurais"
                      value={values.nome_fantasia || ''}
                      onChange={handleChange('nome_fantasia')}
                    />
                  </FieldBlock>

                  <Grid $cols={2}>
                    <FieldBlock>
                      <Label>Razão social</Label>
                      <TextInput
                        type="text"
                        placeholder="Razão social do sindicato"
                        value={values.razao_social || ''}
                        onChange={handleChange('razao_social')}
                      />
                    </FieldBlock>
                    <FieldBlock>
                      <Label>CNPJ</Label>
                      <TextInput
                        type="text"
                        placeholder="00.000.000/0000-00"
                        value={values.cnpj || ''}
                        onChange={handleChange('cnpj')}
                      />
                    </FieldBlock>
                  </Grid>

                  <FieldBlock>
                    <Label>Tipo *</Label>
                    <Select
                      value={values.type || ''}
                      onChange={handleChange('type')}
                    >
                      <option value="">Selecione o tipo</option>
                      <option value="rural">Rural</option>
                      <option value="industrial">Industrial</option>
                      <option value="market">Comércio</option>
                      <option value="public-service">Serviço Público</option>
                    </Select>
                  </FieldBlock>
                </Section>

                {/* 2 · Contato */}
                <Section>
                  <SectionHead>
                    <span className="badge">
                      <FiPhone size={16} />
                    </span>
                    <div>
                      <h4>Contato</h4>
                      <span className="subtitle">
                        Como o sindicato pode ser contactado
                      </span>
                    </div>
                  </SectionHead>

                  <Grid $cols={2}>
                    <FieldBlock>
                      <Label>Telefone</Label>
                      <TextInput
                        type="text"
                        placeholder="(00) 00000-0000"
                        value={values.phone || ''}
                        onChange={handleChange('phone')}
                      />
                    </FieldBlock>
                    <FieldBlock>
                      <Label>E-mail</Label>
                      <TextInput
                        type="email"
                        placeholder="contato@sindicato.com.br"
                        value={values.email || ''}
                        onChange={handleChange('email')}
                      />
                    </FieldBlock>
                  </Grid>
                </Section>

                {/* 3 · Endereço */}
                <Section>
                  <SectionHead>
                    <span className="badge">
                      <FiMapPin size={16} />
                    </span>
                    <div>
                      <h4>Endereço</h4>
                      <span className="subtitle">
                        Localização da sede do sindicato
                      </span>
                    </div>
                  </SectionHead>

                  <Grid $cols={4}>
                    <FieldBlock>
                      <Label>CEP</Label>
                      <TextInput
                        type="text"
                        placeholder="00000-000"
                        value={values.postal_code || ''}
                        onChange={handleChange('postal_code')}
                      />
                    </FieldBlock>
                    <FieldBlock $span={2}>
                      <Label>Endereço</Label>
                      <TextInput
                        type="text"
                        placeholder="Rua, número"
                        value={values.address || ''}
                        onChange={handleChange('address')}
                      />
                    </FieldBlock>
                    <FieldBlock>
                      <Label>Complemento</Label>
                      <TextInput
                        type="text"
                        placeholder="Sala, andar"
                        value={values.address2 || ''}
                        onChange={handleChange('address2')}
                      />
                    </FieldBlock>
                  </Grid>

                  <Grid $cols={2}>
                    <FieldBlock>
                      <Label>Estado</Label>
                      <Select
                        value={values.state || ''}
                        onChange={e => {
                          setFieldValue('state', e.target.value);
                          setFieldValue('city_id', undefined);
                          handleLoadCities(e.target.value);
                        }}
                      >
                        <option value="">Selecione</option>
                        {states.map(state => (
                          <option key={state.uf} value={state.uf}>
                            {state.nome}
                          </option>
                        ))}
                      </Select>
                    </FieldBlock>
                    <FieldBlock>
                      <Label>Cidade</Label>
                      <Select
                        value={values.city_id ?? ''}
                        onChange={handleChange('city_id')}
                        disabled={!values.state}
                      >
                        <option value="">Selecione</option>
                        {cities.map(city => (
                          <option key={city.id} value={city.id}>
                            {city.nome}
                          </option>
                        ))}
                      </Select>
                    </FieldBlock>
                  </Grid>
                </Section>

                {/* 4 · Acordo e identidade visual */}
                <Section>
                  <SectionHead>
                    <span className="badge">
                      <FiPercent size={16} />
                    </span>
                    <div>
                      <h4>Acordo e identidade visual</h4>
                      <span className="subtitle">
                        Percentual de desconto em folha e logotipo do sindicato
                      </span>
                    </div>
                  </SectionHead>

                  <Grid $cols={2}>
                    <FieldBlock>
                      <Label>Porcentagem de desconto *</Label>
                      <MaskedInputWrapper>
                        <NumberFormat
                          suffix="%"
                          max={100}
                          maxLength={4}
                          placeholder="Ex.: 1%"
                          value={values.discount_percentage}
                          className="form-control"
                          name="discount_percentage"
                          onChange={handleChange('discount_percentage')}
                        />
                      </MaskedInputWrapper>
                    </FieldBlock>

                    <FieldBlock>
                      <Label>Logotipo do sindicato</Label>
                      <LogoUpload $hasImage={!!featuredImgSrc}>
                        <input
                          type="file"
                          accept="image/png, image/jpeg, image/jpg"
                          onChange={(event: any) => {
                            setFieldValue('avatar', event.target.files[0]);
                            handleImageUpload(event);
                          }}
                        />
                        <div className="preview">
                          {featuredImgSrc ? (
                            <img src={featuredImgSrc} alt={featuredImgLabel} />
                          ) : (
                            <FiImage size={22} />
                          )}
                        </div>
                        <div className="info">
                          <span className="title">
                            {featuredImgSrc
                              ? 'Alterar imagem'
                              : 'Selecionar imagem'}
                          </span>
                          <span className="desc">
                            {featuredImgSrc
                              ? featuredImgLabel
                              : 'PNG, JPG ou JPEG'}
                          </span>
                        </div>
                      </LogoUpload>
                    </FieldBlock>
                  </Grid>
                </Section>

                <FooterHint>
                  <FiInfo size={13} />
                  <span>
                    As alterações entram em vigor imediatamente após salvar.
                  </span>
                </FooterHint>
              </Modal.Body>

              <Modal.Footer>
                <GhostButton type="button" onClick={handleClose}>
                  Cancelar
                </GhostButton>
                <PrimaryButton type="submit">Salvar alterações</PrimaryButton>
              </Modal.Footer>
            </form>
          )}
        </Formik>
      )}
    </Modal>
  );
};

export default EditSyndicateModal;
